import { FC, useCallback, useEffect, useState } from 'react'
import { PhoneSpecsCondensed } from '../../phones'
import { Box } from '../index'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { PhoneAllCard } from './phone-all-card'
import { useSelector } from 'react-redux'
import { selectCurrentStepId } from '../../state'
import { Steps } from '../../state/interfaces'
import { PhoneCardCondensed } from './phone-card-condensed'

export interface PhoneListCondensedProps {
  phones: PhoneSpecsCondensed[]
  onClick: (phoneId: PhoneSpecsCondensed['phoneId'] | string) => void
}

export const LAZY_LOAD_ITEMS_SIZE = 12

const calculateCursorIndex = ({ phones, startingAt = 0 }: { phones: PhoneSpecsCondensed[]; startingAt?: number }) =>
  Math.min(startingAt + LAZY_LOAD_ITEMS_SIZE, phones.length)

export const PhoneListCondensed: FC<PhoneListCondensedProps> = ({ phones, onClick }) => {
  const { t } = useTranslation()
  const [cursorIndex, setCursorIndex] = useState(calculateCursorIndex({ phones }))
  const currentStepId = useSelector(selectCurrentStepId)

  const displayedPhones = phones
    .map((phone, index) => <PhoneCardCondensed key={`${phone.phoneId}-${index}`} phone={phone} onClick={onClick} />)
    .splice(0, cursorIndex)

  if (displayedPhones.length > 1) {
    const numberOfPhonesToShow = phones.length
    displayedPhones.unshift(
      <PhoneAllCard key="phone-all-card" phones={'nc'} onClick={onClick} phonesToShow={numberOfPhonesToShow} />,
    )
  }

  const noPhonesMessage = useCallback(() => {
    if (displayedPhones.length) {
      return null
    }

    return (
      <Box layerStyle={'error'}>
        <Text>{t('error_no-devices-found') as string}</Text>
        <Text>{t('error_please-go-back') as string}</Text>
      </Box>
    )
  }, [t, displayedPhones, currentStepId])

  useEffect(() => {
    setCursorIndex(calculateCursorIndex({ phones }))
  }, [phones])

  return (
    <Box marginTop={4}>
      {currentStepId !== Steps.CHOOSE_PHONE_OS && noPhonesMessage()}

      <InfiniteScroll
        dataLength={displayedPhones.length}
        next={() => {
          setCursorIndex(calculateCursorIndex({ phones, startingAt: cursorIndex }))
        }}
        hasMore={cursorIndex < phones.length && phones.length > LAZY_LOAD_ITEMS_SIZE}
        hasChildren={displayedPhones.length > 0}
        loader={<h4>Loading...</h4>}
      >
        <Flex marginTop={4} flexWrap="wrap" justifyContent="center">
          {displayedPhones}
        </Flex>
      </InfiniteScroll>
    </Box>
  )
}
